<script lang="ts">
import PaginatedResourceTable from '@shell/components/PaginatedResourceTable.vue';
import NodeMetadata from '@shell/components/lattice/NodeMetadata.vue';
import { applyNodeInventoryApiFilter, nodeMatchesInventory } from '@shell/utils/lattice-node-filters';
import { nodeOverview } from '@shell/utils/lattice-node-overview';
import NodeInfrastructureOverview from '@shell/components/lattice/NodeInfrastructureOverview.vue';
import { Banner } from '@components/Banner';
import { PODS } from '@shell/config/table-headers';
import metricPoller from '@shell/mixins/metric-poller';

import { CAPI as CAPI_ANNOTATIONS } from '@shell/config/labels-annotations.js';

import { defineComponent } from 'vue';
import { ActionFindPageArgs } from '@shell/types/store/dashboard-store.types';
import { FilterArgs, PaginationFilterField, PaginationParamFilter } from '@shell/types/store/pagination.types';

import {
  CAPI,
  MANAGEMENT, METRIC, NODE, NORMAN, POD
} from '@shell/config/types';
import { GROUP_RESOURCES, mapPref } from '@shell/store/prefs';
import { COLUMN_BREAKPOINTS } from '@shell/types/store/type-map';

import { mapGetters } from 'vuex';
import { PagTableFetchPageSecondaryResourcesOpts, PagTableFetchSecondaryResourcesOpts, PagTableFetchSecondaryResourcesReturns } from '@shell/types/components/paginatedResourceTable';

export default defineComponent({
  name: 'ListNode',

  components: {
    PaginatedResourceTable,
    NodeMetadata,
    NodeInfrastructureOverview,
    Banner
  },

  mixins: [metricPoller],

  props: {
    resource: {
      type:     String,
      required: true,
    },

    schema: {
      type:     Object,
      required: true,
    },

    useQueryParamsForSimpleFiltering: {
      type:    Boolean,
      default: false
    },

    listComponent: {
      type:    Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedRole:       '',
      selectedZone:       '',
      roleChoices:        [] as string[],
      zoneChoices:        [] as string[],
      // Pods required for `Pods` column's running pods metrics
      // podConsumedUsage = podConsumed / podConsumedUsage. podConsumed --> pods. allPods.filter((pod) => pod.spec.nodeName === this.name)
      canViewPods:        !!this.$store.getters[`cluster/schemaFor`](POD),
      // Norman node required for Drain/Cordon/Uncordon action
      canViewNormanNodes: !!this.$store.getters[`rancher/schemaFor`](NORMAN.NODE),
      // Mgmt Node required to find Norman node
      canViewMgmtNodes:   !!this.$store.getters[`management/schemaFor`](MANAGEMENT.NODE),
      // Required for ssh / download key actions
      canViewMachines:    !!this.$store.getters[`management/schemaFor`](CAPI.MACHINE),
      // Required for CPU and RAM columns
      canViewNodeMetrics: !!this.$store.getters['cluster/schemaFor'](METRIC.NODE),
    };
  },

  watch: {
    '$route.params.cluster'() {
      this.selectedRole = '';
      this.selectedZone = '';
      this.roleChoices = [];
      this.zoneChoices = [];
      const table: any = this.$refs?.inventory;

      if (table?.pPagination) {
        table.pPagination = applyNodeInventoryApiFilter({ ...table.pPagination, page: 1 }, { role: '', zone: '' });
      }
    },
    kubeNodes: {
      immediate: true,
      handler(nodes: any[]) {
        const overview = nodeOverview(nodes || []);

        this.roleChoices = Array.from(new Set([...this.roleChoices, ...overview.roles.map((entry) => entry.label)]));
        this.zoneChoices = Array.from(new Set([...this.zoneChoices, ...overview.zones.map((entry) => entry.label)]));
      }
    }
  },

  beforeUnmount() {
    // Stop watching pods, nodes and node metrics
    if (this.canViewPods) {
      this.$store.dispatch('cluster/forgetType', POD);
    }

    this.$store.dispatch('cluster/forgetType', NODE);
    this.$store.dispatch('cluster/forgetType', METRIC.NODE);
  },

  computed: {
    ...mapGetters(['currentCluster']),

    kubeNodes() {
      // Note if server side pagination is used this is only the current page
      return this.$store.getters[`cluster/all`](this.resource);
    },

    hasWindowsNodes() {
      // Note if server side pagination is used this is only applicable to the current page
      return (this.kubeNodes || []).some((node: any) => node.status?.nodeInfo?.operatingSystem === 'windows');
    },

    tableGroup: mapPref(GROUP_RESOURCES),

    canPaginate() {
      const args = { id: this.resource?.id || this.resource };

      return this.resource && this.$store.getters[`cluster/paginationEnabled`]?.(args);
    },

    headers() {
      // This is all about adding the pods column... if the user can see pods
      const headers = [...this.$store.getters['type-map/headersFor'](this.schema, false)];

      if (this.canViewPods) {
        headers.splice(headers.length - 1, 0, {
          ...PODS,
          breakpoint: COLUMN_BREAKPOINTS.DESKTOP,
          getValue:   (row: any) => row.podConsumedUsage
        });
      }

      return this.inventoryHeaders(headers);
    },

    paginationHeaders() {
      // This is all about adding the pods column... if the user can see pods

      if (!this.canPaginate) {
        return [];
      }

      const paginationHeaders = [...this.$store.getters['type-map/headersFor'](this.schema, true)];

      if (paginationHeaders) {
        if (this.canViewPods) {
          paginationHeaders.splice(paginationHeaders.length - 1, 0, {
            ...PODS,
            breakpoint: COLUMN_BREAKPOINTS.DESKTOP,
            sort:       false,
            search:     false,
            getValue:   (row: any) => row.podConsumedUsage
          });
        }

        return this.inventoryHeaders(paginationHeaders);
      } else {
        console.warn('Nodes list expects pagination headers but none found'); // eslint-disable-line no-console

        return [];
      }
    },
  },

  methods: {
    filterInventory(rows: any[]) {
      return rows.filter((row) => nodeMatchesInventory(row, { role: this.selectedRole, zone: this.selectedZone }));
    },
    filterInventoryApi(pagination: any) {
      return applyNodeInventoryApiFilter(pagination, { role: this.selectedRole, zone: this.selectedZone });
    },
    async toggleInventoryFilter(kind: 'role' | 'zone', value: string) {
      if (kind === 'role') {
        this.selectedRole = this.selectedRole === value ? '' : value;
      } else {
        this.selectedZone = this.selectedZone === value ? '' : value;
      }
      await this.resetInventoryPage();
    },
    async clearInventoryFilters() {
      this.selectedRole = '';
      this.selectedZone = '';
      await this.resetInventoryPage();
    },
    async resetInventoryPage() {
      const table: any = this.$refs.inventory;
      const sortable = table?.$refs.table?.$refs.table;

      table?.clearSelection();
      sortable?.setPage(1);
      await this.$nextTick();
      if (this.canPaginate && table?.pagination) {
        // Preserve native search, sort and page size while resetting both the UI and fetch page.
        sortable?.debouncedPaginationChanged?.cancel();
        table.debouncedSetPagination?.cancel();
        if (table.paginationFromList) {
          table.paginationFromList = { ...table.paginationFromList, page: 1 };
        }
        table.setPagination(this.filterInventoryApi({ ...table.pagination, page: 1 }));
      }
    },
    inventoryHeaders(headers: any[]) {
      const widths: Record<string, number> = {
        name: 260, roles: 110, version: 100, 'internal-external-ip': 210, age: 90
      };

      return headers.map((header) => widths[header.name] ? { ...header, width: widths[header.name] } : header);
    },

    async loadMetrics() {
      if (!this.canViewNodeMetrics) {
        return;
      }

      if (this.canPaginate) {
        if (!this.kubeNodes.length) {
          return;
        }

        const opt: ActionFindPageArgs = {
          force:      true,
          pagination: new FilterArgs({
            filters: new PaginationParamFilter({
              fields: this.kubeNodes.map((r: any) => new PaginationFilterField({
                field: 'metadata.name',
                value: r.id
              }))
            })
          })
        };

        await this.$store.dispatch('cluster/findPage', {
          type: METRIC.NODE,
          opt
        });
      } else {
        await this.$store.dispatch('cluster/findAll', {
          type: METRIC.NODE,
          opt:  { force: true }
        });
      }

      this.$forceUpdate();
    },

    /**
     * of type PagTableFetchSecondaryResources
     */
    async fetchSecondaryResources({ canPaginate }: PagTableFetchSecondaryResourcesOpts): PagTableFetchSecondaryResourcesReturns {
      if (canPaginate) {
        return;
      }
      const promises = [];

      if (this.canViewMgmtNodes) {
        promises.push(this.$store.dispatch(`management/findAll`, { type: MANAGEMENT.NODE }));
      }

      if (this.canViewNormanNodes) {
        promises.push(this.$store.dispatch(`rancher/findAll`, { type: NORMAN.NODE }));
      }

      if (this.canViewMachines) {
        promises.push(this.$store.dispatch(`management/findAll`, { type: CAPI.MACHINE }));
      }

      if (this.canViewPods) {
        // No need to block on this
        this.$store.dispatch(`cluster/findAll`, { type: POD });
      }

      await Promise.all(promises);
    },

    /**
     * Nodes columns need other resources in order to show data in some columns
     *
     * In the paginated world we want to restrict the fetch of those resources to only the one's we need
     *
     * So when we have a page.... use those entries as filters when fetching the other resources
     *
     * of type PagTableFetchPageSecondaryResources
     */
    async fetchPageSecondaryResources({ canPaginate, force, page }: PagTableFetchPageSecondaryResourcesOpts) {
      if (!page?.length) {
        return;
      }

      if (this.canViewMgmtNodes && this.canViewNormanNodes) {
        if (this.canViewNormanNodes) {
          // Ideally we only fetch the nodes we need....
          this.$store.dispatch(`rancher/findAll`, { type: NORMAN.NODE });
        }

        // We only fetch mgmt node to get norman node. We only fetch node to get node actions
        // See https://github.com/rancher/dashboard/issues/10743
        const opt: ActionFindPageArgs = {
          force,
          pagination: new FilterArgs({
            filters: PaginationParamFilter.createMultipleFields(page.map((r: any) => new PaginationFilterField({
              field: 'status.nodeName',
              value: r.id
            }))),
          })
        };

        this.$store.dispatch(`management/findPage`, { type: MANAGEMENT.NODE, opt });
      }

      if (this.canViewMachines) {
        const namespace = this.currentCluster.provClusterId?.split('/')[0];

        if (namespace) {
          const opt: ActionFindPageArgs = {
            force,
            namespaced: namespace,
            pagination: new FilterArgs({
              filters: PaginationParamFilter.createMultipleFields(
                page.reduce((res: PaginationFilterField[], r: any ) => {
                  const name = r.metadata?.annotations?.[CAPI_ANNOTATIONS.MACHINE_NAME];

                  if (name) {
                    res.push(new PaginationFilterField({
                      field: 'metadata.name',
                      value: name,
                    }));
                  }

                  return res;
                }, [])
              )
            })
          };

          this.$store.dispatch(`management/findPage`, { type: CAPI.MACHINE, opt });
        }
      }

      if (this.canViewPods) {
        // Note - fetching pods for current page could be a LOT still (probably max of 3k - 300 pods per node x 100 nodes in a page)
        const opt: ActionFindPageArgs = {
          force,
          pagination: new FilterArgs({
            filters: PaginationParamFilter.createMultipleFields(
              page.map((r: any) => new PaginationFilterField({
                field: 'spec.nodeName',
                value: r.id,
              }))
            )
          })
        };

        this.$store.dispatch(`cluster/findPage`, { type: POD, opt });
      }

      // Fetch metrics given the current page
      this.loadMetrics();
    },
  },
});
</script>

<template>
  <div class="node-infrastructure">
    <p
      v-if="!listComponent"
      class="node-description"
    >
      Compute infrastructure for your Kubernetes cluster. Select nodes to manage scheduling or open a node for its configuration.
    </p>
    <NodeInfrastructureOverview
      v-if="!listComponent"
      :nodes="kubeNodes"
      :cluster-id="$route.params.cluster"
      :cluster-name="currentCluster?.nameDisplay || currentCluster?.spec?.displayName || $route.params.cluster"
      :paginated="canPaginate"
      :partial-scope="canPaginate"
      :selected-role="selectedRole"
      :selected-zone="selectedZone"
      :role-choices="roleChoices"
      :zone-choices="zoneChoices"
      @toggle-role="toggleInventoryFilter('role', $event)"
      @toggle-zone="toggleInventoryFilter('zone', $event)"
      @clear-filters="clearInventoryFilters"
    />
    <Banner
      v-if="hasWindowsNodes"
      color="info"
      :label="t('cluster.custom.registrationCommand.windowsWarning')"
    />
    <PaginatedResourceTable
      ref="inventory"
      :local-filter="filterInventory"
      :api-filter="filterInventoryApi"
      v-bind="$attrs"
      :schema="schema"
      :headers="headers"
      :paginationHeaders="paginationHeaders"
      :sub-rows="true"
      :fetchSecondaryResources="fetchSecondaryResources"
      :fetchPageSecondaryResources="fetchPageSecondaryResources"
      :use-query-params-for-simple-filtering="useQueryParamsForSimpleFiltering"
      data-testid="cluster-node-list"
    >
      <template #sub-row="{fullColspan, row, onRowMouseEnter, onRowMouseLeave}">
        <tr
          class="taints sub-row"
          :class="{'empty-taints': ! row.displayTaintsAndLabels}"
          @mouseenter="onRowMouseEnter"
          @mouseleave="onRowMouseLeave"
        >
          <template v-if="row.displayTaintsAndLabels">
            <td aria-hidden="true" />
            <td aria-hidden="true" />
            <td :colspan="fullColspan-2">
              <NodeMetadata
                :node="row"
                :taints-label="t('node.list.nodeTaint')"
                :labels-label="t('node.list.nodeLabels')"
              />
            </td>
          </template>
          <td
            v-else
            :colspan="fullColspan"
          />
        </tr>
      </template>
    </PaginatedResourceTable>
  </div>
</template>

<style lang='scss' scoped>

.node-description {
  margin: -4px 0 20px;
  max-width: 850px;
  color: var(--dev-muted, var(--body-text));
  font-size: 13px;
  line-height: 1.5;
}

.node-infrastructure :deep(.sortable-table) {
  background: var(--dev-surface, var(--body-bg));
  border: 1px solid var(--dev-border, var(--border));
  border-radius: 8px;
  font-size: 13px;
}

.node-infrastructure :deep(.sortable-table table > tbody > tr.main-row > td) {
  padding-top: 10px;
  padding-bottom: 8px;
  vertical-align: middle;
}

.node-infrastructure :deep(.sortable-table th) {
  padding-top: 12px;
  padding-bottom: 12px;
  font-size: 12px;
  color: var(--dev-muted, var(--body-text));
}

.node-infrastructure :deep(.sortable-table .main-row .name) {
  font-weight: 500;
  line-height: 1.45;
}

.node-infrastructure :deep(.col-link-detail) {
  min-width: 220px;
  word-break: normal;
  overflow-wrap: normal;
  font-weight: 500;
  line-height: 1.4;
}
.node-infrastructure :deep(.col-internal-external-ip) {
  min-width: 180px;
}
.node-infrastructure :deep(.sortable-table table > tbody > tr.taints > td) {
  padding-top: 0;
  padding-bottom: 8px;
  vertical-align: top;
}
.node-infrastructure :deep(.sortable-table table > tbody > tr.empty-taints) {
  height: 0;
  line-height: 0;
}
.node-infrastructure :deep(.sortable-table table > tbody > tr.empty-taints > td) {
  padding: 0;
}
</style>
